/**
 * HTTP calls behind the `/oauth/consent` screen (PX-W4.8, decision D7;
 * PX-W6.1: migrated off raw `fetch` onto the shared `apiClient`, the ONE
 * transport — boundary-enforced by `src/test/transportBoundary.test.ts`).
 *
 * Two endpoints on the PX backend (`:9112` in dev, reached through the shared
 * client's base URL):
 *
 *  - `POST /api/v1/auth/login` body `{ email, password }`
 *      -> `{ accessToken, user }`, plus an httpOnly `refreshToken` cookie set
 *        by the server. The access token is handed to the auth service's
 *        in-memory token store (`setAccessToken`), so the transport's
 *        interceptor carries the Bearer from here on — this module never
 *        holds the token itself.
 *  - `POST /api/v1/oauth/consent` body `{ decision, client_id, redirect_uri,
 *      scope, state, code_challenge, resource }` (the Bearer comes from the
 *      interceptor) -> `{ redirect }`, the absolute URL to navigate to next.
 *
 * The consent call is the true security gate of the authorization flow: the
 * backend mints the single-use code against the principal in that Bearer, so a
 * consent POST without it is a 401 and mints nothing
 * (`OAuthAuthorizationServerController.consent`).
 *
 * PASSWORD ON THE WIRE. The backend's `LoginRequest.password` is annotated
 * `@JsonDeserialize(using = StringDecryptionDeSerializer.class)`, i.e. it
 * ACCEPTS an RSA-wrapped value. It does not require one: the deserializer
 * passes the text through untouched when the key is the `changeme` dev
 * placeholder (`application.conf`) or when decryption fails, and the password
 * check then runs against the plaintext. The xelerate reference client wraps
 * the field with `jsencrypt` using a public key its Play view injects into
 * `index.html`; PX's `SpaController` injects no such key and the client does
 * not bundle `jsencrypt`, so there is nothing to encrypt with and the field
 * goes as plaintext JSON over the same TLS channel as every other request.
 * When PX gains the injected key, wrap the value in {@link signIn} - it is the
 * one call that sends a credential.
 */

import { apiClient, isApiError } from '@/services'
import { setAccessToken } from '@/features/auth/services/authService.http'

/**
 * The user's decision for the requesting OAuth client. These are the backend's
 * exact wire values - its decision gate is fail-closed, so anything other than
 * the literal `approve` or `deny` is a 400 that mints no code.
 */
export type ConsentDecision = 'approve' | 'deny'

/**
 * The validated authorization-request params, threaded verbatim from the
 * `/oauth/consent` query string into the backing POST so the issued code is
 * re-bound to exactly this client + challenge + resource (anti confused-deputy).
 */
export interface ConsentRequestParams {
  /** The requesting client's id (`client_id`). */
  clientId: string
  /** The client's registered redirect target (`redirect_uri`), exact-match. */
  redirectUri: string
  /** The requested scope - a single coarse `mcp` in v1. */
  scope: string
  /** Opaque round-trip value echoed back to the client (`state`). */
  state: string
  /** PKCE S256 challenge (`code_challenge`), bound to the issued code. */
  codeChallenge: string
  /** RFC 8707 resource indicator (`resource`) - the MCP audience. */
  resource: string
}

/**
 * The consent endpoint's response: the absolute URL to navigate to next. The
 * field is `redirect`, mirroring the backend wire contract verbatim - reading
 * any other name yields `undefined`, and navigating to that strands the user
 * on `/oauth/undefined`.
 */
export interface ConsentResponse {
  redirect: string
}

/** Why a call failed, in terms the page can branch on without parsing prose. */
export type ConsentApiReason =
  | 'invalid-credentials'
  | 'two-factor-required'
  | 'unauthorized'
  | 'failed'

/** A failed backend call, carrying the HTTP status and a branchable reason. */
export class ConsentApiError extends Error {
  readonly status: number
  readonly reason: ConsentApiReason

  constructor(status: number, reason: ConsentApiReason) {
    super(`px consent api: ${reason} (${status})`)
    this.name = 'ConsentApiError'
    this.status = status
    this.reason = reason
  }
}

interface LoginResponse {
  twoFactorRequired?: boolean
  accessToken?: string
}

/**
 * Map a shared-client rejection (always an `ApiError`) onto this surface's
 * reason vocabulary: 401/423 means the caller-supplied credential/authority
 * was refused (`onUnauthorized` names which reason), anything else is
 * `failed`.
 */
const toConsentError = (error: unknown, onUnauthorized: ConsentApiReason): ConsentApiError => {
  const status = isApiError(error) ? error.status : 0
  if (status === 401 || status === 423) {
    return new ConsentApiError(status, onUnauthorized)
  }
  return new ConsentApiError(status, 'failed')
}

/**
 * Exchange a credential for a session. The access token lands in the auth
 * service's IN-MEMORY store (never web storage), and the shared transport's
 * interceptor attaches it as the Bearer from here on.
 *
 * @param email - The account's email address.
 * @param password - The plaintext password (see the module note on the wire
 *   format). It is passed straight into the request body and is never logged.
 * @throws ConsentApiError `invalid-credentials` on a 401/423, `two-factor-required`
 *   when the account needs a second factor, `failed` otherwise.
 */
export async function signIn(email: string, password: string): Promise<void> {
  let payload: LoginResponse
  try {
    payload = await apiClient.post<LoginResponse>('/auth/login', { email, password })
  } catch (error) {
    throw toConsentError(error, 'invalid-credentials')
  }

  if (payload.twoFactorRequired === true) {
    throw new ConsentApiError(200, 'two-factor-required')
  }
  if (typeof payload.accessToken !== 'string' || payload.accessToken === '') {
    throw new ConsentApiError(200, 'failed')
  }
  setAccessToken(payload.accessToken)
}

/**
 * Submit the consent decision for the requesting OAuth client. The Bearer is
 * carried by the shared transport's interceptor (bound to the principal whose
 * token {@link signIn} stored), which is what binds the minted code to that
 * user.
 *
 * @param decision - `approve` (mints an authorization code) or `deny` (yields
 *   the `access_denied` redirect).
 * @param params - The validated authorization-request params.
 * @returns The absolute URL the browser must navigate to next.
 * @throws ConsentApiError `unauthorized` when the session is not accepted,
 *   `failed` on any other rejection.
 */
export async function submitConsent(
  decision: ConsentDecision,
  params: ConsentRequestParams
): Promise<ConsentResponse> {
  let payload: Partial<ConsentResponse>
  try {
    payload = await apiClient.post<Partial<ConsentResponse>>('/oauth/consent', {
      decision,
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      scope: params.scope,
      state: params.state,
      code_challenge: params.codeChallenge,
      resource: params.resource
    })
  } catch (error) {
    throw toConsentError(error, 'unauthorized')
  }

  if (typeof payload.redirect !== 'string' || payload.redirect === '') {
    throw new ConsentApiError(200, 'failed')
  }
  return { redirect: payload.redirect }
}
